import { useMemo } from 'react';
import { useUser } from '@store';
import { useGetCurrentUserCustomerIdsQuery } from '../queries/getCurrentUserCustomerIds.query.generated';
import { readableRooftopLinks, resolveCustomerIds, resolveHasLiteTier, resolveHasDriverCapableRooftop } from '../utils/customerScopeUtils';

export interface ScopedCustomer {
  id: number;
  name: string;
  tier: string;
  effective_config?: Record<string, any> | null;
}

/**
 * Resolves the set of customer IDs, full customer objects, and Lite-tier flag for the
 * current user — scoped to the customers linked via their usertocustomers records.
 *
 * Admin users don't reach the pages that consume this (user/driver management routes
 * block admins — see ProtectedRoute `blockAdmin`); staff manage users in Admin 5.
 */
export function useCustomerScope() {
  const user = useUser();
  const userId = user?.profile?.id;

  // Fetch customers linked to this user via usertocustomers
  const { data: customerData, loading } = useGetCurrentUserCustomerIdsQuery({
    variables: { userId: userId! },
    skip: !userId,
  });

  const customerIds = useMemo(
    () => resolveCustomerIds(customerData?.usertocustomers),
    [customerData],
  );

  // The ids above keep every linked rooftop, since the team query filters users by id.
  // Anything that reads the rooftop itself uses only the ones the viewer can read.
  const readableLinks = useMemo(() => readableRooftopLinks(customerData?.usertocustomers), [customerData]);

  const hasLiteTier = useMemo(
    () => resolveHasLiteTier(readableLinks),
    [readableLinks],
  );

  // True when at least one in-scope rooftop may manage its own drivers (lite OR Mobile Service).
  const hasDriverCapableRooftop = useMemo(
    () => resolveHasDriverCapableRooftop(readableLinks as any),
    [readableLinks],
  );

  const customers = useMemo<ScopedCustomer[]>(() => {
    return readableLinks.map(utc => ({
      id: Number(utc.customer_id),
      name: utc.customer.name ?? '',
      // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
      tier: (utc.customer as any).tier ?? '',
      effective_config: (utc.customer as any).effective_config ?? null,
    }));
  }, [readableLinks]);

  return {
    customerIds,
    customers,
    hasLiteTier,
    hasDriverCapableRooftop,
    loading,
  };
}
